import { create } from 'zustand';
import type {
  Character,
  Episode,
  Scene,
  SceneImage,
  SceneVideo,
} from '@/types';

interface EpisodeStore {
  episode: Episode | null;
  scenes: Scene[];
  characters: Character[];
  images: Record<string, SceneImage>;
  videos: Record<string, SceneVideo>;
  isLoading: boolean;
  loadError: string | null;

  reset: () => void;
  setBundle: (bundle: {
    episode: Episode;
    characters: Character[];
    scenes: Scene[];
    images: SceneImage[];
    videos: SceneVideo[];
  }) => void;
  setEpisode: (episode: Episode | null) => void;
  setScenes: (scenes: Scene[]) => void;
  upsertScene: (scene: Scene) => void;
  removeScene: (id: string) => void;
  upsertImage: (image: SceneImage) => void;
  upsertVideo: (video: SceneVideo) => void;
  setLoading: (isLoading: boolean) => void;
  setLoadError: (error: string | null) => void;
}

export const useEpisodeStore = create<EpisodeStore>((set) => ({
  episode: null,
  scenes: [],
  characters: [],
  images: {},
  videos: {},
  isLoading: false,
  loadError: null,

  reset: () =>
    set({
      episode: null,
      scenes: [],
      characters: [],
      images: {},
      videos: {},
      isLoading: false,
      loadError: null,
    }),
  setBundle: ({ episode, characters, scenes, images, videos }) =>
    set({
      episode,
      characters,
      scenes,
      images: Object.fromEntries(images.map((img) => [img.id, img])),
      videos: Object.fromEntries(videos.map((vid) => [vid.id, vid])),
      isLoading: false,
      loadError: null,
    }),
  setEpisode: (episode) => set({ episode }),
  setScenes: (scenes) => set({ scenes }),
  upsertScene: (scene) =>
    set((s) => {
      const existing = s.scenes.findIndex((x) => x.id === scene.id);
      if (existing === -1) return { scenes: [...s.scenes, scene] };
      const next = s.scenes.slice();
      next[existing] = scene;
      return { scenes: next };
    }),
  removeScene: (id) => set((s) => ({ scenes: s.scenes.filter((x) => x.id !== id) })),
  upsertImage: (image) => set((s) => ({ images: { ...s.images, [image.id]: image } })),
  upsertVideo: (video) => set((s) => ({ videos: { ...s.videos, [video.id]: video } })),
  setLoading: (isLoading) => set({ isLoading }),
  setLoadError: (loadError) => set({ loadError }),
}));
